import { useEffect, useReducer } from "react";
import type { VideoSpec } from "../../types";
import { handleEditorError } from "../../Nooks";
import { renderDigest } from "../../Utils";
import { generateImageAndPlaceholders } from "./loader";
import type { LivePreviewState } from "./reducer";
import { LivePreviewActionType, livePreviewInitialState, livePreviewReducer } from "./reducer";
import { SskyErrorCode } from "../../../../../common/errors";
import type { VideoAspectRatio } from "../../../../../common/external/videoSpec";
import type { LivePreviewClient } from "./livePreviewRequestAnalytics";

export type LivePreviewFromVideoSpecType = LivePreviewState & {
    showFallback: boolean;
    showSceneTransitioning: boolean;
};

export type UseLivePreviewFromVideoSpecParams = {
    livePreviewClient: LivePreviewClient;
    videoSpec: VideoSpec;
    sceneId: string;
};

export const useLivePreviewFromVideoSpec = (params: UseLivePreviewFromVideoSpecParams): LivePreviewFromVideoSpecType => {
    const { sceneId, livePreviewClient, videoSpec } = params;

    const [state, dispatch] = useReducer(livePreviewReducer, livePreviewInitialState);

    //Effect to make request to RaaS
    useEffect(() => {
        let isCancelled: boolean = false;

        const dispatchError = (e?: any): void => {
            const error = e || new Error("Generic Error");
            dispatch({
                type: LivePreviewActionType.RenderError,
                payload: { error }
            });
            handleEditorError({
                error: error?.lastError || error,
                sskyCode: SskyErrorCode.LivePreviewError,
                details: error?.allErrors || { requestId: videoSpec.requestid }
            });
        };

        if (videoSpec.lineup.length === 0) {
            dispatchError(new Error("videoSpec lineup is empty"));
        }

        if (sceneId) {
            //reset live preview when program aspect ratio changes, videoSpec.output.videoAspectRatio is equal to aspectRatio
            if (state.videoAspectRatio !== videoSpec.output.videoAspectRatio as VideoAspectRatio) {
                dispatch({
                    type: LivePreviewActionType.Reset,
                    payload: undefined
                });
            }
            dispatch({
                type: LivePreviewActionType.RenderStart,
                payload: { sceneId }
            });

            generateImageAndPlaceholders(sceneId, videoSpec, livePreviewClient)
                .then(imageAndPlaceholders => {
                    if (!isCancelled) {
                        const imageSrc = imageAndPlaceholders.imageSrc;
                        const viewport = imageAndPlaceholders.viewport;

                        dispatch({
                            type: LivePreviewActionType.RenderSuccess,
                            payload: { imageSrc, viewport, canvases: {}, renderedPlaceholders: [], foregroundLayer: null }
                        });
                    }
                }, error => {
                    if (!isCancelled) {
                        dispatchError(error);
                    }
                });
        }
        else {
            dispatch({
                type: LivePreviewActionType.Reset,
                payload: undefined
            });
        }

        return () => {
            isCancelled = true;
        };
    }, [sceneId, videoSpec && renderDigest(videoSpec), dispatch, livePreviewClient]);

    const showFallback = !state.imageSrc || state.pending || state.error;
    const showSceneTransitioning = showFallback && state.fallbackSceneId && state.fallbackSceneId !== state.sceneId;

    return {
        ...state,
        showFallback,
        showSceneTransitioning
    };
};
